import { type WidgetConfig } from './config'
import { WidgetStage } from './rendering/stage'
import { AvatarRenderer } from './rendering/avatarRenderer'
import { fetchWidgetSession, flagVisitor, type WidgetSessionResponse } from './api/backend'
import { RealtimeSession, type RealtimeTool } from './engine/realtimeSession'
import { AudioForwarder } from './engine/audioForwarder'
import { AnamSession } from './engine/anamSession'
import { DomAnalyzer } from './engine/domAnalyzer'
import { ToolHandler } from './engine/toolHandler'
import { PageActions } from './actions/pageActions'
import { OverlayActions } from './actions/overlayActions'
import { ConversationLogger } from './api/logger'
import { RRWebRecorder } from './vision/rrwebRecorder'
import { VisionWSClient } from './vision/wsClient'
import type { V1WsClient } from './vision/v1WsClient'
type WsClient = VisionWSClient | V1WsClient
import { ActionExecutor } from './vision/actionExecutor'

// Liquid Glass UI components
import {
  injectTokens,
  autoDetectAndApply,
  AvatarFrame,
  GlassSpeechBubble,
  GlassChatPill,
  GlassOverlayRenderer,
  GlassControls,
  GlassConfirmPrompt,
  GlassHighlightRenderer,
  GlassStageBackdrop,
} from './ui'

type WidgetState = 'idle' | 'teasing' | 'connecting' | 'active' | 'minimized' | 'destroyed'
type AvatarState = 'idle' | 'listening' | 'thinking' | 'speaking'

/** Delay after slide-in completes before the speech bubble appears. */
const BUBBLE_DELAY_MS = 800
/** Max session duration (10 minutes). */
const MAX_DURATION_MS = 10 * 60 * 1000
/** Warning before max duration (9 minutes). */
const MAX_DURATION_WARN_MS = 9 * 60 * 1000
/** Inactivity threshold before prompting (60 seconds). */
const INACTIVITY_PROMPT_MS = 60 * 1000
/** Inactivity threshold before auto-close (90 seconds total). */
const INACTIVITY_CLOSE_MS = 90 * 1000

/** Tool definitions sent to the Realtime API. */
const WIDGET_TOOLS: RealtimeTool[] = [
  {
    type: 'function',
    name: 'page_action',
    description:
      'Interact with the webpage the visitor is viewing. You can scroll to elements, highlight elements, click buttons/links, fill form fields, or read the page content. Describe what you want to do in natural language. Call this to understand the page, guide the visitor, or take actions on their behalf.',
    parameters: {
      type: 'object',
      properties: {
        instruction: {
          type: 'string',
          description:
            "What to do on the page, e.g. 'scroll to pricing section', 'highlight the sign-up button', 'fill the email field with test@example.com', 'read the page to understand what it contains'",
        },
      },
      required: ['instruction'],
    },
  },
  {
    type: 'function',
    name: 'show_text_card',
    description: 'Display a floating text card overlay near the avatar with additional information for the visitor. Use for key points, explanations, or summaries that benefit from visual display.',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Card title' },
        body: { type: 'string', description: 'Card body text' },
      },
      required: ['title', 'body'],
    },
  },
  {
    type: 'function',
    name: 'show_image_card',
    description: 'Display a floating image card overlay with an optional caption.',
    parameters: {
      type: 'object',
      properties: {
        image_url: { type: 'string', description: 'URL of the image to display' },
        caption: { type: 'string', description: 'Optional caption below the image' },
      },
      required: ['image_url'],
    },
  },
  {
    type: 'function',
    name: 'show_bullet_list',
    description: 'Display a styled list of key points as a floating overlay card.',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'List title' },
        items: { type: 'array', items: { type: 'string' }, description: 'List items' },
      },
      required: ['title', 'items'],
    },
  },
  {
    type: 'function',
    name: 'dismiss_overlays',
    description: 'Dismiss all currently visible overlay cards.',
    parameters: { type: 'object', properties: {}, required: [] },
  },
  {
    type: 'function',
    name: 'get_skill_instructions',
    description:
      'Retrieve the full playbook for one of your configured skills. Call this when you determine a skill is relevant to the current conversation — it returns detailed guidance on what to say, tone, and what to avoid. Use the skill name exactly as listed in the "Your Skills" section of your instructions.',
    parameters: {
      type: 'object',
      properties: {
        skill_name: {
          type: 'string',
          description: 'The exact skill name as listed in Your Skills (e.g. "Convert Visitor", "Get Started").',
        },
      },
      required: ['skill_name'],
    },
  },
  {
    type: 'function',
    name: 'end_conversation',
    description:
      'Gracefully end the current conversation. Call this after two failed attempts to steer an off-topic visitor back to the platform\'s purpose. Before calling, deliver your exit message naturally in speech — do not include it as a parameter. Set flag=true to mark this visitor as non-serious so they are not interrupted by the agent on future visits.',
    parameters: {
      type: 'object',
      properties: {
        flag: {
          type: 'boolean',
          description: 'If true, flag this visitor as non-serious. The widget will be fully suppressed on their future visits.',
        },
        reason: {
          type: 'string',
          description: 'Short reason for logging. E.g. "visitor not interested in platform".',
        },
      },
      required: ['flag'],
    },
  },
]

export class HolostaffWidget {
  private config: WidgetConfig
  private stage: WidgetStage | null = null
  private avatar: AvatarRenderer | null = null
  private state: WidgetState = 'idle'
  private avatarState: AvatarState = 'idle'
  private scrollHandler: (() => void) | null = null
  private idleVideoUrl: string | null = null
  private idleImageUrl: string | null = null
  private bubbleTimer: ReturnType<typeof setTimeout> | null = null

  // Conversation session
  private realtimeSession: RealtimeSession | null = null
  private audioForwarder: AudioForwarder | null = null
  private anamSession: AnamSession | null = null
  private sessionResponse: WidgetSessionResponse | null = null

  // Page actions (Issue 9) + overlays (Issue 10)
  private domAnalyzer: DomAnalyzer | null = null
  private toolHandler: ToolHandler | null = null

  // Timers (Issue 11)
  private maxDurationTimer: ReturnType<typeof setTimeout> | null = null
  private maxDurationWarnTimer: ReturnType<typeof setTimeout> | null = null
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null
  private inactivityCloseTimer: ReturnType<typeof setTimeout> | null = null
  private sessionStartTime = 0

  // Conversation logging (Issue 12)
  private logger: ConversationLogger | null = null

  // Vision loop (v3) — observer is owned externally (Issue 26), presenter hooks into it
  private rrwebRecorder: RRWebRecorder | null = null
  private visionWs: WsClient | null = null
  private externalObserver = false
  private actionExecutor: ActionExecutor | null = null
  private visionContext: string | null = null
  private staffDisplayName = 'Assistant'
  private staffAvatarUrl: string | null = null
  private hasHadConversation = false
  private visitorId: string
  private widgetPrompts: string[] | null
  /** Set when the agent calls end_conversation — processed on the next response.done. */
  private pendingEndAfterResponse: { flag: boolean; reason: string } | null = null
  private evalMode: boolean

  // Liquid Glass UI components
  private avatarFrame: AvatarFrame | null = null
  private bubble: GlassSpeechBubble | null = null
  private chatPill: GlassChatPill | null = null
  private overlayRenderer: GlassOverlayRenderer | null = null
  private controls: GlassControls | null = null
  private confirmationPrompt: GlassConfirmPrompt | null = null
  private highlightRenderer: GlassHighlightRenderer | null = null
  private stageBackdrop: GlassStageBackdrop | null = null

  // Issue 24: Home Base ↔ Stage elements
  private stageEl: HTMLDivElement | null = null
  private homePlaceholder: HTMLDivElement | null = null
  private homeObserver: IntersectionObserver | null = null
  private homeVisible = true
  private homeEverSeen = false
  private pillProtected = false // skip observer hide for one cycle after conversation ends

  constructor(config: WidgetConfig, widgetPrompts?: string[] | null, evalMode?: boolean) {
    this.config = config
    this.widgetPrompts = widgetPrompts ?? null
    this.evalMode = evalMode ?? false
    this.visitorId = this.getOrCreateVisitorId()
  }

  private paused = false

  /** Whether the widget is in inline embed mode. */
  get isInline(): boolean {
    return this.config.mode === 'inline' && !!this.config.container
  }

  /** The current inline container selector (for SPA route change comparison). */
  get activeContainer(): string | null {
    return this.config.container
  }

  async init(): Promise<void> {
    injectTokens()

    this.stage = new WidgetStage()
    await this.stage.init(this.config.mode, this.config.container)

    this.avatar = new AvatarRenderer(this.stage.stageWidth, this.stage.stageHeight)
    this.stage.app.stage.addChild(this.avatar.container)

    await this.loadIdleAsset()

    // Liquid Glass: avatar frame wraps the widget root
    this.avatarFrame = new AvatarFrame()
    if (this.stage.rootElement) {
      autoDetectAndApply(this.stage.rootElement)
    }

    // Liquid Glass: speech bubble (DOM element, positioned above widget root)
    this.bubble = new GlassSpeechBubble(this.widgetPrompts ?? undefined)
    this.bubble.onClick = () => this.onAvatarClick()
    if (this.stage.rootElement) {
      this.stage.rootElement.style.position = 'relative'
      this.stage.rootElement.appendChild(this.bubble.el)
    }

    this.avatar.setInteractive(() => this.onAvatarClick())

    // Persistent pill — used in both inline and floating modes.
    // In inline mode it appears when the home card scrolls out of view.
    // In floating mode it's always potentially visible (no home card to observe).
    this.chatPill = new GlassChatPill()
    this.chatPill.init(this.staffAvatarUrl, this.idleVideoUrl, this.staffDisplayName)
    this.chatPill.onClick = () => this.onAvatarClick()
    // Long-press on the pill → explicit "quiet for this visit". Server
    // marks the session silent; no more proactive pops until reload.
    this.chatPill.onLongPress = () => {
      const v1 = this.visionWs as { sendSilenceGesture?: () => void } | null
      v1?.sendSilenceGesture?.()
      this.chatPill?.showAmbient()
      console.log('[HolostaffWidget] long-press: quiet for this visit')
    }

    if (this.isInline) {
      // After 5s, show pill if the home card is not visible
      setTimeout(() => {
        const isIdle = this.state === 'idle' || this.state === 'teasing'
        if (isIdle && !this.homeVisible && this.chatPill?.state === 'hidden') {
          this.chatPill.showNamed()
        }
      }, 5000)

      this.startHomeObserver()
    } else {
      // Floating mode: no home card — pill is always the ambient presence.
      // homeVisible stays false so ActionExecutor always routes suggestions to the pill.
      this.homeVisible = false
    }

    this.setupTrigger()
    this.startVisionLoop()

    // Flush conversation log on page unload (e.g. user navigates away mid-conversation)
    window.addEventListener('beforeunload', () => {
      if (this.state === 'active' || this.state === 'connecting') {
        this.logger?.flush()
      }
    })

    console.log('[HolostaffWidget] Initialized', {
      staffId: this.config.staffId,
      trigger: this.config.trigger,
      visionEnabled: !!this.config.visionWsUrl,
    })
  }

  private async loadIdleAsset(): Promise<void> {
    if (!this.avatar || !this.stage) return

    // Floating mode with an orchestrator observer (v3 vision loop OR v1
    // Bowtie): no idle asset renders in the main stage. The avatar
    // animation appears only inside the chat pill (initialized
    // separately with the same idleVideoUrl). Skipping here keeps the
    // stage empty until a conversation starts (when Anam live video
    // loads in).
    if (!this.isInline && (this.config.visionWsUrl || this.config.v1Mode)) return

    if (this.idleVideoUrl) {
      try {
        if (this.isInline && this.stage.inlineTarget) {
          // Inline mode: render a glass card with video on top + label below
          const target = this.stage.inlineTarget

          // Ensure the target container is a positioning context
          target.style.position = 'relative'

          // Create the glass card wrapper — absolute positioned to fill container
          const card = document.createElement('div')
          card.className = 'hs-inline-card'
          card.setAttribute('data-hs-theme', 'dark')
          Object.assign(card.style, {
            position: 'absolute',
            inset: '0',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 'var(--hs-radius-lg)',
            overflow: 'hidden',
            background: 'var(--hs-glass-bg)',
            backdropFilter: 'blur(var(--hs-glass-blur)) saturate(var(--hs-glass-saturate))',
            WebkitBackdropFilter: 'blur(var(--hs-glass-blur)) saturate(var(--hs-glass-saturate))',
            border: '1px solid var(--hs-glass-border)',
            boxShadow: 'var(--hs-glass-shadow)',
            cursor: 'pointer',
            padding: 'var(--hs-space-3)',
            boxSizing: 'border-box',
          })

          // Video section — inset with visible border inside the card
          const videoWrap = document.createElement('div')
          Object.assign(videoWrap.style, {
            flex: '1',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: 'var(--hs-radius-md)',
            border: '1px solid var(--hs-glass-border)',
          })

          const vid = document.createElement('video')
          vid.src = this.idleVideoUrl
          vid.autoplay = true
          vid.loop = true
          vid.muted = true
          vid.playsInline = true
          vid.setAttribute('playsinline', '')
          Object.assign(vid.style, {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          })
          videoWrap.appendChild(vid)
          card.appendChild(videoWrap)

          // Label section below the video
          const labelSection = document.createElement('div')
          Object.assign(labelSection.style, {
            padding: 'var(--hs-space-3) var(--hs-space-4)',
            textAlign: 'center',
          })
          const title = document.createElement('div')
          Object.assign(title.style, {
            color: 'var(--hs-text-primary)',
            fontSize: 'var(--hs-text-base)',
            fontWeight: 'var(--hs-weight-semibold)',
            fontFamily: 'var(--hs-font)',
            lineHeight: 'var(--hs-line-height)',
          })
          title.textContent = `${this.staffDisplayName}, your AI copilot`
          labelSection.appendChild(title)

          const subtitle = document.createElement('div')
          Object.assign(subtitle.style, {
            color: 'var(--hs-text-tertiary)',
            fontSize: 'var(--hs-text-xs)',
            fontFamily: 'var(--hs-font)',
            marginTop: '2px',
          })
          subtitle.textContent = 'Powered by Holostaff'
          labelSection.appendChild(subtitle)

          card.appendChild(labelSection)

          // Click handler
          card.addEventListener('click', () => this.onAvatarClick())

          target.appendChild(card)

          // Hide the PixiJS canvas in inline mode — the glass card IS the idle display
          if (this.stage.rootElement) {
            this.stage.rootElement.style.display = 'none'
          }
        } else {
          // Floating mode: use PixiJS avatar renderer
          const root = this.stage.rootElement
          if (!root) throw new Error('Widget root not available')
          await this.avatar.loadIdleVideo(this.idleVideoUrl, root)
        }
        return
      } catch {
        console.warn('[HolostaffWidget] Failed to load idle video, trying image fallback')
      }
    }
    if (this.idleImageUrl) {
      await this.avatar.loadIdleImage(this.idleImageUrl)
    } else {
      console.warn('[HolostaffWidget] No idle video or image configured')
    }
  }

  setIdleVideo(url: string): void {
    this.idleVideoUrl = url
  }

  setIdleImage(url: string): void {
    this.idleImageUrl = url
  }

  /**
   * Attach an externally-created observer. In v3 legacy this is rrweb +
   * VisionWSClient; in v1 the recorder is null and `ws` is a
   * V1WsClient that speaks /v1/widget directly. Either way the widget
   * treats the WS as its source of intervention commands.
   */
  setObserver(recorder: RRWebRecorder | null, ws: WsClient): void {
    this.rrwebRecorder = recorder
    this.visionWs = ws
    this.externalObserver = true
  }

  /** Set staff display info for the chat-again pill. */
  setStaffInfo(name: string, avatarUrl: string | null): void {
    this.staffDisplayName = name
    this.staffAvatarUrl = avatarUrl
  }

  // -----------------------------------------------------------------------
  // Eval mode — write to window.__HS_EVAL_STATE__ for the eval runner
  // -----------------------------------------------------------------------

  private updateEvalState(patch: Record<string, unknown>): void {
    if (!this.evalMode) return
    const state = (window as any).__HS_EVAL_STATE__
    if (!state) return
    Object.assign(state, patch)
  }

  // -----------------------------------------------------------------------
  // Trigger & Teaser
  // -----------------------------------------------------------------------

  private setupTrigger(): void {
    // Inline mode: always show avatar immediately — it's part of the page layout.
    if (this.isInline) {
      this.showAvatarOnly()
      return
    }

    // Floating mode with an orchestrator observer (v3 vision loop OR v1
    // Bowtie): nothing renders up-front. The observer runs in the
    // background; when it decides to intervene, the suggestion surfaces
    // via the chat pill. The avatar only appears if the visitor accepts
    // the suggestion and the conversation starts.
    if (this.config.visionWsUrl || this.config.v1Mode) return

    // Legacy (v2) floating triggers — no vision loop configured.
    switch (this.config.trigger) {
      case 'scroll':
        this.setupScrollTrigger()
        break
      case 'delay':
        setTimeout(() => this.showTeaser(), this.config.delayMs)
        break
      case 'immediate':
        this.showTeaser()
        break
    }
  }

  private setupScrollTrigger(): void {
    this.scrollHandler = () => {
      const scrollY = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollPct = docHeight > 0 ? scrollY / docHeight : 0

      if (scrollPct >= this.config.scrollThreshold && this.state === 'idle') {
        this.showTeaser()
      } else if (scrollPct < this.config.scrollThreshold * 0.5 && this.state === 'teasing') {
        this.hideTeaser()
      }
    }
    window.addEventListener('scroll', this.scrollHandler, { passive: true })
  }

  /** v3: Show avatar immediately without speech bubble prompts. */
  private showAvatarOnly(): void {
    if (this.state !== 'idle' || !this.avatar || !this.stage) return
    this.state = 'teasing'
    this.avatar.slideIn(this.stage.app.ticker)
    // No speech bubble — VLM will send contextual messages via suggest_intervention
  }

  private showTeaser(): void {
    if (this.state !== 'idle' || !this.avatar || !this.stage) return
    this.state = 'teasing'
    this.avatar.slideIn(this.stage.app.ticker)
    this.bubbleTimer = setTimeout(() => this.bubble?.start(), BUBBLE_DELAY_MS)
  }

  private hideTeaser(): void {
    if (this.state !== 'teasing' || !this.avatar || !this.stage) return
    this.state = 'idle'
    if (this.bubbleTimer) { clearTimeout(this.bubbleTimer); this.bubbleTimer = null }
    this.bubble?.stop()
    this.avatar.slideOut(this.stage.app.ticker)
  }

  // -----------------------------------------------------------------------
  // Conversation lifecycle (Issues 5-8)
  // -----------------------------------------------------------------------

  private async onAvatarClick(): Promise<void> {
    // Allow click from teasing state (v2 scroll trigger) or idle state (v3 accepted suggestion)
    if (this.state !== 'teasing' && this.state !== 'idle') return

    this.state = 'connecting'
    // Don't stop the observer — it keeps homeVisible accurate.
    // The observer callback checks state === 'idle' so it won't show/hide pill during conversation.
    this.bubble?.stop()
    this.bubble?.hideStatic()

    // Dismiss any active suggestion (toast or card overlay)
    this.actionExecutor?.destroy()
    this.actionExecutor = null

    if (this.isInline) {
      // Show "Connecting..." in the pill
      this.chatPill?.showConnecting()

      // Freeze idle video in place — don't move to stage yet (wait for Anam video)
      const idleVid = this.stage?.inlineTarget?.querySelector('.hs-inline-card video') as HTMLVideoElement
      if (idleVid) idleVid.pause()
    } else {
      // Floating mode
      if (this.stage?.rootElement) {
        this.stage.rootElement.style.display = ''
      }
      const idleVid = document.querySelector('#holostaff-widget-root video') as HTMLVideoElement
      if (idleVid) idleVid.pause()
      this.bubble?.showStatic('Connecting...')
    }
    console.log('[HolostaffWidget] Starting conversation...')

    try {
      // 1. Fetch session tokens
      this.sessionResponse = await fetchWidgetSession(
        this.config.staffId,
        this.config.backendUrl,
        this.visitorId,
      )
      console.log('[HolostaffWidget] Session tokens received')

      // 1b. Create conversation logger
      this.logger = new ConversationLogger({
        staffId: this.config.staffId,
        staffName: this.sessionResponse.staff.name,
        backendUrl: this.config.backendUrl,
      })

      // 2. Connect Anam first (so avatar video is ready when AI speaks)
      await this.connectAnam()

      // 3. Connect Realtime API
      await this.connectRealtimeApi()

      this.state = 'active'
      this.setAvatarState('listening')
      this.sessionStartTime = Date.now()
      this.bubble?.hideStatic() // Hide "Connecting..."
      this.updateEvalState({ conversationStarted: true })

      // 5. Show controls
      this.showControls()

      // 6. Start session timers
      this.startSessionTimers()

      // 7. Pause vision loop during conversation
      this.visionWs?.sendConversationStarted()
      this.rrwebRecorder?.pause()

      console.log('[HolostaffWidget] Conversation active')

    } catch (err) {
      console.error('[HolostaffWidget] Failed to start conversation:', err)
      // Capture error in eval state so the eval runner can surface it
      this.updateEvalState({
        conversationError: (err as Error)?.message ?? String(err),
        conversationErrorAt: new Date().toISOString(),
      })
      this.bubble?.hideStatic()
      this.endConversation()
    }
  }

  // -----------------------------------------------------------------------
  // Issue 24: Home Base ↔ Stage lifecycle
  // -----------------------------------------------------------------------

  /** Move the conversation to a fixed bottom-center glass stage (inline mode). */
  private moveToStage(): void {
    if (!this.isInline || !this.stage?.inlineTarget) return

    // 1. Hide the glass card and its contents
    const card = this.stage.inlineTarget.querySelector('.hs-inline-card') as HTMLElement
    if (card) card.style.display = 'none'
    this.stage.inlineTarget.querySelectorAll('video').forEach(el => {
      if (el instanceof HTMLVideoElement) el.pause()
    })

    // 2. Glass-styled placeholder in the inline container
    this.homePlaceholder = document.createElement('div')
    this.homePlaceholder.setAttribute('data-hs-theme', 'dark')
    Object.assign(this.homePlaceholder.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      color: 'var(--hs-text-secondary)',
      fontSize: 'var(--hs-text-sm)',
      fontFamily: 'var(--hs-font)',
      textAlign: 'center',
      padding: 'var(--hs-space-4)',
      boxSizing: 'border-box',
    })
    this.homePlaceholder.innerHTML = 'Agent is active below&nbsp;&darr;'
    this.stage.inlineTarget.appendChild(this.homePlaceholder)

    // 3. Create glass stage backdrop at bottom-center
    this.stageBackdrop = new GlassStageBackdrop()
    this.stageEl = this.stageBackdrop.el
    this.stageBackdrop.show()

    console.log('[HolostaffWidget] Moved to glass stage (bottom-center)')
  }

  /** Return the avatar from the stage back to the inline home container. */
  private returnToHome(): void {
    // Remove glass stage
    this.stageBackdrop?.destroy()
    this.stageBackdrop = null
    this.stageEl = null

    // Remove placeholder from inline container
    this.homePlaceholder?.remove()
    this.homePlaceholder = null

    if (!this.stage?.inlineTarget) return

    // Restore the glass card and its video (PixiJS rootElement stays hidden in inline mode)
    const card = this.stage.inlineTarget.querySelector('.hs-inline-card') as HTMLElement
    if (card) card.style.display = ''

    this.stage.inlineTarget.querySelectorAll('.hs-inline-card video').forEach(el => {
      (el as HTMLElement).style.display = ''
      if (el instanceof HTMLVideoElement) el.play()
    })

    console.log('[HolostaffWidget] Returned to home (inline container)')
  }

  /**
   * Watch the inline home container — show chat pill when it scrolls out
   * of viewport, hide pill when it's visible again.
   */
  private startHomeObserver(): void {
    this.stopHomeObserver()
    const target = this.stage?.inlineTarget
    if (!target) return

    // Synchronous initial check — don't default to true
    const rect = target.getBoundingClientRect()
    this.homeVisible = rect.top < window.innerHeight && rect.bottom > 0

    this.homeObserver = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = this.homeVisible
        this.homeVisible = entry.isIntersecting

        if (this.homeVisible && !this.homeEverSeen) {
          this.homeEverSeen = true
        }

        const isIdle = this.state === 'idle' || this.state === 'teasing'
        if (isIdle && this.chatPill) {
          if (this.homeVisible) {
            // Skip one hide cycle after conversation ends (pill should stay visible briefly)
            if (this.pillProtected) {
              this.pillProtected = false
              return
            }
            // Card visible → hide pill
            this.chatPill.hide()
          } else if (!this.homeVisible && wasVisible && this.homeEverSeen) {
            // Card just scrolled away (after being seen) → show named pill
            this.chatPill.showNamed()
          } else if (!this.homeVisible && !this.homeEverSeen) {
            // Card not yet seen, not visible → show named if not already
            if (this.chatPill.state === 'hidden') {
              this.chatPill.showNamed()
            }
          }
        }
      },
      { threshold: 0.1 },
    )

    this.homeObserver.observe(target)
  }

  private stopHomeObserver(): void {
    this.homeObserver?.disconnect()
    this.homeObserver = null
  }

  // -----------------------------------------------------------------------
  // Anam connection (avatar rendering, BYO audio mode)
  // -----------------------------------------------------------------------

  private async connectAnam(): Promise<void> {
    if (!this.sessionResponse) return

    this.anamSession = new AnamSession({
      sessionToken: this.sessionResponse.anamSessionToken,
      inlineMode: this.isInline && !!this.stageBackdrop,
    })

    this.anamSession.onConnected = () => {
      console.log('[HolostaffWidget] Anam connected')
    }

    this.anamSession.onVideoReady = (videoEl) => {
      console.log('[HolostaffWidget] Anam avatar video ready')
      this.bubble?.hideStatic()
      // Hide the pill when video conversation starts
      this.chatPill?.hide()

      if (this.isInline) {
        // Issue 24: Now move to stage — crossfade idle card out, stage in
        this.moveToStage()
        this.stageBackdrop?.appendContent(videoEl)
      } else if (this.avatar && this.stage) {
        // Floating mode: render via PixiJS as before
        this.avatar.setLiveVideo(videoEl, this.stage.app.ticker)
      }
    }

    this.anamSession.onTalkStreamInterrupted = () => {
      console.log('[HolostaffWidget] Anam talk stream interrupted')
    }

    this.anamSession.onDisconnected = (reason) => {
      console.log('[HolostaffWidget] Anam disconnected:', reason)
      this.endConversation()
    }

    this.anamSession.onError = (err) => {
      console.error('[HolostaffWidget] Anam error:', err.message)
    }

    await this.anamSession.connect()
    console.log('[HolostaffWidget] Anam session ready')
  }

  // -----------------------------------------------------------------------
  // Realtime API connection + conversation orchestration (Issues 5, 6, 8)
  // -----------------------------------------------------------------------

  private async connectRealtimeApi(): Promise<void> {
    if (!this.sessionResponse) return

    // Build a quick page summary for the system prompt
    const pageTitle = document.title || ''
    const pageUrl = location.href
    const metaDesc = document.querySelector('meta[name="description"]')?.getAttribute('content') || ''
    // Grab visible headings for a structural overview
    const headings = Array.from(document.querySelectorAll('h1, h2, h3')).slice(0, 10)
      .map(h => h.textContent?.trim()).filter(Boolean).join(' | ')

    // Append page interaction instructions + vision context
    let instructions = (this.sessionResponse.staff.instructions ?? '') +
      `\n\n## Current Page\nTitle: ${pageTitle}\nURL: ${pageUrl}` +
      (metaDesc ? `\nDescription: ${metaDesc}` : '') +
      (headings ? `\nSections: ${headings}` : '') +
      '\n\n## Page Interaction\n' +
      'You can interact with the webpage the visitor is viewing by calling the page_action tool.\n' +
      'Call it with instruction "read the page content" to get a detailed DOM snapshot.\n' +
      'Then you can scroll to elements, highlight them, click links/buttons, or fill form fields.\n' +
      'All actions except form filling require the visitor to confirm before executing.'

    // Inject vision context if the conversation was triggered by an accepted suggestion
    if (this.visionContext) {
      instructions += '\n\n## Context from observing the user\'s session\n' + this.visionContext
      this.visionContext = null // consume it
    }

    this.realtimeSession = new RealtimeSession({
      ephemeralKey: this.sessionResponse.realtimeEphemeralKey,
      webrtcUrl: this.sessionResponse.realtimeWebrtcUrl,
      voice: this.sessionResponse.staff.voiceId,
      instructions,
      tools: WIDGET_TOOLS,
    })

    // Set up page action handlers
    this.domAnalyzer = new DomAnalyzer()
    this.highlightRenderer = new GlassHighlightRenderer()
    this.confirmationPrompt = new GlassConfirmPrompt()

    this.realtimeSession.onConnected = () => {
      console.log('[HolostaffWidget] Realtime API connected')
      // Inject opening text as the avatar's first message
      const openingText = this.sessionResponse?.staff.openingText
      if (openingText?.trim()) {
        this.realtimeSession?.sendJSON({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [{ type: 'input_text', text: '(conversation started — greet the visitor now)' }],
          },
        })
        this.realtimeSession?.sendJSON({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'assistant',
            content: [{ type: 'text', text: openingText }],
          },
        })
        this.realtimeSession?.sendJSON({ type: 'response.create' })
      }
    }

    this.realtimeSession.onDisconnected = () => {
      console.log('[HolostaffWidget] Realtime API disconnected')
      if (this.state === 'active' || this.state === 'connecting') {
        this.endConversation()
      }
    }

    // Remote audio track → AudioForwarder → Anam (lip-sync + playback)
    this.realtimeSession.onRemoteTrack = async (stream) => {
      console.log('[HolostaffWidget] Remote audio track → starting audio forwarder')
      this.audioForwarder = new AudioForwarder()

      this.audioForwarder.onAudioChunk = (pcmData) => {
        // Forward PCM audio to Anam for lip-sync and audio playback
        this.anamSession?.sendAudioChunk(pcmData)
      }

      await this.audioForwarder.start(stream)
    }

    // Create tool handler (needs realtimeSession to exist)
    this.overlayRenderer = new GlassOverlayRenderer()
    this.toolHandler = new ToolHandler({
      realtimeSession: this.realtimeSession,
      domAnalyzer: this.domAnalyzer!,
      pageActions: new PageActions(this.domAnalyzer!, this.highlightRenderer!),
      overlayActions: new OverlayActions(this.overlayRenderer),
      confirmationPrompt: this.confirmationPrompt!,
      staffId: this.config.staffId,
      backendUrl: this.config.backendUrl,
      skills: this.sessionResponse.skills ?? {},
    })

    // Log tool calls
    this.toolHandler.onToolCall = (tool, args) => {
      this.logger?.addAction(tool, args)
    }

    // Graceful exit requested by the agent — wait for next response.done before tearing down
    this.toolHandler.onEndConversation = (flag, reason) => {
      console.log(`[HolostaffWidget] end_conversation requested (flag=${flag})`)
      this.pendingEndAfterResponse = { flag, reason }
    }

    // Data channel message handling (Issue 8 orchestration + Issue 9 tools)
    this.realtimeSession.onDataMessage = (msg) => {
      this.handleDataChannelMessage(msg)
    }

    this.realtimeSession.onError = (err) => {
      console.error('[HolostaffWidget] Realtime error:', err.message)
      this.endConversation()
    }

    await this.realtimeSession.connect()
  }

  // -----------------------------------------------------------------------
  // Data channel message handling (Issue 8)
  // -----------------------------------------------------------------------

  private handleDataChannelMessage(msg: any): void {
    // Delegate tool call messages to the tool handler first
    if (this.toolHandler?.handleMessage(msg)) return

    const type = msg.type as string

    switch (type) {
      // --- Session lifecycle ---
      case 'session.created':
      case 'session.updated':
        console.log('[HolostaffWidget] Data:', type)
        break

      // --- VAD: user started speaking → interrupt avatar ---
      case 'input_audio_buffer.speech_started':
        console.log('[HolostaffWidget] User started speaking')
        this.anamSession?.interrupt()
        this.audioForwarder?.pause()
        this.setAvatarState('listening')
        this.resetInactivityTimer()
        break

      // --- VAD: user stopped speaking → thinking ---
      case 'input_audio_buffer.speech_stopped':
        console.log('[HolostaffWidget] User stopped speaking')
        this.setAvatarState('thinking')
        break

      // --- AI response started → resume audio forwarding ---
      case 'response.created':
        console.log('[HolostaffWidget] AI response started')
        this.audioForwarder?.resume()
        this.setAvatarState('speaking')
        break

      // --- AI response done → signal end of audio turn to Anam ---
      case 'response.done':
        console.log('[HolostaffWidget] AI response done')
        this.anamSession?.endSequence()
        this.setAvatarState('listening')
        this.extractAiTranscript(msg.response)
        // Check if agent requested end_conversation — fire-and-forget flag, then close
        if (this.pendingEndAfterResponse) {
          const { flag, reason } = this.pendingEndAfterResponse
          this.pendingEndAfterResponse = null
          if (flag) {
            flagVisitor(this.config.staffId, this.visitorId, reason, this.config.backendUrl)
          }
          this.endConversation()
        } else {
          this.resetInactivityTimer()
        }
        break

      // --- Transcription (user speech) ---
      case 'conversation.item.input_audio_transcription.completed':
        console.log('[HolostaffWidget] User said:', msg.transcript)
        this.logger?.addUserMessage(msg.transcript ?? '')
        break

      // --- Errors ---
      case 'error':
        console.error('[HolostaffWidget] Realtime error event:', msg.error)
        break

      // --- Response cancelled/failed ---
      case 'response.cancelled':
      case 'response.canceled':
      case 'response.failed':
        console.warn('[HolostaffWidget] Response ended:', type)
        this.anamSession?.endSequence()
        this.audioForwarder?.pause()
        this.setAvatarState('listening')
        break
    }
  }

  /** Extract AI-generated text from a response.done payload for logging. */
  private extractAiTranscript(response: any): void {
    if (!response?.output) return
    for (const item of response.output) {
      if (item.type === 'message' && item.content) {
        for (const part of item.content) {
          const text = part.transcript ?? part.text
          if (!text) continue
          this.logger?.addStaffMessage(text)
          // Eval mode: expose avatar transcripts so the eval runner can feed
          // them to the simulated visitor for multi-turn conversation.
          if (this.evalMode) {
            const state = (window as any).__HS_EVAL_STATE__
            if (state) {
              if (!Array.isArray(state.avatarTranscripts)) state.avatarTranscripts = []
              state.avatarTranscripts.push(text)
            }
          }
        }
      }
    }
  }

  // -----------------------------------------------------------------------
  // Avatar state management (Issue 8)
  // -----------------------------------------------------------------------

  private setAvatarState(newState: AvatarState): void {
    if (this.avatarState === newState) return
    const prev = this.avatarState
    this.avatarState = newState
    console.log(`[HolostaffWidget] Avatar: ${prev} → ${newState}`)

    switch (newState) {
      case 'listening':
      case 'thinking':
      case 'speaking':
      case 'idle':
        // Avatar pose is driven by Anam's native lip-sync (no explicit state commands needed)
        break
    }
  }

  // -----------------------------------------------------------------------
  // Controls — minimize, restore, close (Issue 11)
  // -----------------------------------------------------------------------

  private showControls(): void {
    this.controls = new GlassControls()

    this.controls.onMinimize = () => {
      this.state = 'minimized'
      // Hide avatar but keep session alive
      if (this.avatar && this.stage) {
        this.avatar.slideOut(this.stage.app.ticker)
      }
      this.overlayRenderer?.dismissAll()
      console.log('[HolostaffWidget] Minimized')
    }

    this.controls.onRestore = () => {
      this.state = 'active'
      if (this.avatar && this.stage) {
        this.avatar.slideIn(this.stage.app.ticker)
      }
      console.log('[HolostaffWidget] Restored')
    }

    this.controls.onClose = () => {
      console.log('[HolostaffWidget] Close button clicked')
      this.endConversation()
    }

    this.controls.show()
  }

  // -----------------------------------------------------------------------
  // Session timers — max duration + inactivity (Issue 11)
  // -----------------------------------------------------------------------

  private startSessionTimers(): void {
    // Max duration warning at 9 minutes
    this.maxDurationWarnTimer = setTimeout(() => {
      console.log('[HolostaffWidget] Session approaching max duration')
      this.realtimeSession?.sendJSON({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'system',
          content: [{ type: 'input_text', text: 'The session will end in about one minute. Please wrap up the conversation gracefully.' }],
        },
      })
      this.realtimeSession?.sendJSON({ type: 'response.create' })
    }, MAX_DURATION_WARN_MS)

    // Max duration hard close at 10 minutes
    this.maxDurationTimer = setTimeout(() => {
      console.log('[HolostaffWidget] Max session duration reached')
      this.endConversation()
    }, MAX_DURATION_MS)

    // Start inactivity tracking
    this.resetInactivityTimer()
  }

  /** Reset the inactivity timer (called whenever there's activity). */
  private resetInactivityTimer(): void {
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer)
    if (this.inactivityCloseTimer) clearTimeout(this.inactivityCloseTimer)

    // Prompt after 60s of silence
    this.inactivityTimer = setTimeout(() => {
      if (this.state !== 'active') return
      console.log('[HolostaffWidget] Inactivity — prompting user')
      this.realtimeSession?.sendJSON({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'system',
          content: [{ type: 'input_text', text: 'The visitor has been silent for a while. Ask them "Still there?" in a friendly way.' }],
        },
      })
      this.realtimeSession?.sendJSON({ type: 'response.create' })

      // Auto-close after another 30s
      this.inactivityCloseTimer = setTimeout(() => {
        if (this.state !== 'active') return
        console.log('[HolostaffWidget] Inactivity — auto-closing')
        this.endConversation()
      }, INACTIVITY_CLOSE_MS - INACTIVITY_PROMPT_MS)
    }, INACTIVITY_PROMPT_MS)
  }

  private clearSessionTimers(): void {
    if (this.maxDurationTimer) { clearTimeout(this.maxDurationTimer); this.maxDurationTimer = null }
    if (this.maxDurationWarnTimer) { clearTimeout(this.maxDurationWarnTimer); this.maxDurationWarnTimer = null }
    if (this.inactivityTimer) { clearTimeout(this.inactivityTimer); this.inactivityTimer = null }
    if (this.inactivityCloseTimer) { clearTimeout(this.inactivityCloseTimer); this.inactivityCloseTimer = null }
  }

  // -----------------------------------------------------------------------
  // End conversation
  // -----------------------------------------------------------------------

  private endConversation(): void {
    this.updateEvalState({ conversationEnded: true })

    // Flush conversation log (fire-and-forget)
    this.logger?.flush()
    this.logger = null

    // Clear all timers
    this.clearSessionTimers()

    // Hide controls
    this.controls?.hide()
    this.controls = null

    this.audioForwarder?.stop()
    this.audioForwarder = null

    this.anamSession?.close()
    this.anamSession = null

    this.realtimeSession?.close()
    this.realtimeSession = null

    this.sessionResponse = null
    this.avatarState = 'idle'
    this.pendingEndAfterResponse = null

    // Clean up page action + overlay resources
    this.toolHandler = null
    this.domAnalyzer = null
    this.highlightRenderer?.clearAll()
    this.highlightRenderer = null
    this.confirmationPrompt?.dismiss()
    this.confirmationPrompt = null
    this.overlayRenderer?.dismissAll()
    this.overlayRenderer = null


    // Crossfade back to idle video
    if (this.avatar && this.stage) {
      this.avatar.clearLiveVideo(this.stage.app.ticker)
    }

    // Resume vision loop with conversation summary
    const conversationSummary = this.buildConversationSummary()
    this.visionWs?.sendConversationEnded(conversationSummary)
    this.rrwebRecorder?.resume()

    this.hasHadConversation = true
    this.state = 'idle'

    // Remove any leftover Anam video elements
    document.querySelectorAll('[id^="anam-avatar-wrapper"]').forEach(el => el.remove())
    document.querySelectorAll('video[id^="anam-avatar-video"]').forEach(el => el.remove())

    if (this.isInline) {
      // Issue 24: Return from stage to home (inline container) — restores glass card + video
      this.returnToHome()

      // Always restore and show the pill after conversation
      // Protect from immediate observer hide (card is in viewport after returnToHome)
      this.pillProtected = true
      this.chatPill?.restoreName()
      this.chatPill?.showNamed()

      // Re-attach action executor so future interventions work
      this.attachActionExecutor()
    } else {
      // Floating mode: hide everything and show the minimal chat pill
      if (this.avatar) {
        this.avatar.container.visible = false
      }
      if (this.stage?.rootElement) {
        this.stage.rootElement.style.display = 'none'
      }
      this.showChatPill()
    }

    console.log('[HolostaffWidget] Conversation ended')
  }

  private showChatPill(): void {
    if (!this.chatPill) {
      this.chatPill = new GlassChatPill()
      this.chatPill.init(this.staffAvatarUrl, this.idleVideoUrl, this.staffDisplayName)
      this.chatPill.onClick = () => this.onAvatarClick()
    }
    this.chatPill.showNamed()
  }

  private hideChatPill(): void {
    this.chatPill?.hide()
  }

  // -----------------------------------------------------------------------
  // Vision loop (v3)
  // -----------------------------------------------------------------------

  private startVisionLoop(): void {
    if (this.externalObserver && this.visionWs) {
      // Issue 26: Observer already running externally — just attach the action executor
      this.attachActionExecutor()
      console.log('[HolostaffWidget] Action executor attached to external observer')
      return
    }

    // v1 Bowtie mode — index.ts owns the V1WsClient and passes it via
    // setObserver(). If we reach here without an external observer in
    // v1 mode, fall through silently rather than spin up a parallel
    // legacy /vision path against the same visitor.
    if (this.config.v1Mode) return

    // Fallback: create our own observer (no page targeting)
    const wsUrl = this.config.visionWsUrl
    if (!wsUrl) return

    this.rrwebRecorder = new RRWebRecorder()

    this.visionWs = new VisionWSClient(wsUrl, {
      staffId: this.config.staffId,
      visitorId: this.visitorId,
      pageUrl: location.href,
      pageTitle: document.title,
    })

    this.rrwebRecorder.onEvent = (event) => {
      this.visionWs?.sendRRWebEvent(event)
    }

    this.attachActionExecutor()

    this.visionWs.connect()

    if (this.config.visionConsent === 'required') {
      console.log('[HolostaffWidget] Vision loop started (recording deferred — consent required)')
    } else {
      this.rrwebRecorder.start()
      console.log('[HolostaffWidget] Vision loop started', { visitorId: this.visitorId })
    }
  }

  /** Wire the action executor to handle suggest_intervention from the vision WS. */
  private attachActionExecutor(): void {
    if (!this.visionWs) return

    this.actionExecutor = new ActionExecutor({
      wsClient: this.visionWs,
      getWidgetState: () => this.state,
      getHomeVisible: () => this.homeVisible,
      inlineTarget: this.stage?.inlineTarget ?? null,
      pill: this.chatPill,
      disableAutoDismiss: this.evalMode,
    })

    this.actionExecutor.onInterventionShown = (goalId, message) => {
      this.updateEvalState({ interventionFired: true, skillId: goalId, openingMessage: message })

      // Eval mode: expose a programmatic accept function so the eval runner
      // can trigger the conversation flow without AI-based clicking.
      if (this.evalMode) {
        ;(window as any).__HS_EVAL_ACCEPT__ = () => {
          this.actionExecutor?.accept()
        }
      }
    }

    this.actionExecutor.onAccept = (context) => {
      console.log('[HolostaffWidget] Suggestion accepted — starting conversation with vision context')
      this.visionContext = context
      this.onAvatarClick()
    }
  }

  private stopVisionLoop(): void {
    this.actionExecutor?.destroy()
    this.actionExecutor = null

    // Only stop observer if we own it (not external)
    if (!this.externalObserver) {
      this.rrwebRecorder?.stop()
      this.rrwebRecorder = null
      this.visionWs?.close()
      this.visionWs = null
    }
  }

  private buildConversationSummary(): string {
    return this.logger?.getSummary() ?? 'Conversation ended (no transcript available).'
  }

  private getOrCreateVisitorId(): string {
    const COOKIE_NAME = 'hs_visitor_id'
    const existing = document.cookie
      .split('; ')
      .find(c => c.startsWith(`${COOKIE_NAME}=`))
      ?.split('=')[1]

    if (existing) return existing

    const id = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
    document.cookie = `${COOKIE_NAME}=${id}; max-age=${365 * 24 * 60 * 60}; path=/; SameSite=Lax`
    return id
  }

  // -----------------------------------------------------------------------
  // Page targeting — pause / resume / container swap (Issues 22, 23)
  // -----------------------------------------------------------------------

  /** Hide the presenter when SPA navigates away from a matched page. Observer keeps running. */
  pause(): void {
    if (this.paused) return
    this.paused = true
    console.log('[HolostaffWidget] Presenter paused (page not targeted — observer continues)')

    // End any active conversation
    if (this.state === 'active' || this.state === 'connecting') {
      this.endConversation()
    }

    // Hide all visible elements
    if (this.stage?.rootElement) {
      this.stage.rootElement.style.display = 'none'
    }
    this.hideChatPill()
    this.bubble?.stop()

    // Detach action executor so interventions are suppressed while hidden
    this.actionExecutor?.destroy()
    this.actionExecutor = null
  }

  /** Show the presenter when SPA navigates to a matched page. */
  resume(): void {
    if (!this.paused) return
    this.paused = false
    console.log('[HolostaffWidget] Presenter resumed (page targeted)')

    if (this.stage?.rootElement) {
      this.stage.rootElement.style.display = ''
    }

    // Re-attach action executor so interventions work again
    this.attachActionExecutor()

    // Re-trigger the teaser/idle state
    if (this.state === 'idle' || this.state === 'teasing') {
      this.state = 'idle'
      this.setupTrigger()
    }
  }

  /** Switch inline container when SPA navigates to a page with a different container. */
  updateContainer(container: string | null): void {
    const newMode = container ? 'inline' : 'floating'
    const changed = this.config.container !== container || this.config.mode !== newMode

    if (!changed) return

    console.log('[HolostaffWidget] Updating container:', container ?? '(floating)')
    this.config = { ...this.config, mode: newMode, container }

    // If the stage is already initialized, we need to re-init it with the new container
    // For now, destroy and re-create (conversations are ended on page nav anyway)
    if (this.stage) {
      // End conversation if active
      if (this.state === 'active' || this.state === 'connecting') {
        this.endConversation()
      }

      this.bubble?.destroy()
      this.avatar?.destroy()
      this.stage.destroy()
      this.stage = null
      this.avatar = null
      this.bubble = null
      this.state = 'idle'

      // Re-init
      this.init()
    }
  }

  // -----------------------------------------------------------------------
  // Cleanup
  // -----------------------------------------------------------------------

  destroy(): void {
    this.state = 'destroyed'
    this.endConversation()
    this.hideChatPill()
    this.stopHomeObserver()
    this.stopVisionLoop()

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler)
    }
    if (this.bubbleTimer) {
      clearTimeout(this.bubbleTimer)
    }
    this.bubble?.destroy()
    this.avatar?.destroy()
    this.stage?.destroy()
    this.bubble = null
    this.avatar = null
    this.stage = null
  }
}
